import React, { useState, useEffect } from 'react';
import {
  Box,
  Tabs,
  Tab,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Alert
} from '@mui/material';
import { Delete as DeleteIcon, Add as AddIcon, Edit as EditIcon } from '@mui/icons-material';

// Получаем IP-адрес сервера из URL
const API_URL = (window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1")
  ? window.location.protocol + '//' + window.location.hostname + ':9000'
  : '';

function StructureManagement({ userRole }) {
  const [activeTab, setActiveTab] = useState(0);
  const [stages, setStages] = useState([]);
  const [categories, setCategories] = useState([]);
  const [subcategories, setSubcategories] = useState([]);
  const [projects, setProjects] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [dialogType, setDialogType] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    stage_id: ''
  });
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [selectedStageId, setSelectedStageId] = useState('');
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [editType, setEditType] = useState('');

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (selectedStageId) {
      fetchCategories(selectedStageId);
    }
  }, [selectedStageId]);

  const fetchData = async () => {
    try {
      const currentUser = JSON.parse(localStorage.getItem('user'));
      if (!currentUser) throw new Error('Користувач не знайдений');
      const token = localStorage.getItem('token');
      const headers = { 'Authorization': `Bearer ${token}` };
      const [stagesRes, subcategoriesRes, projectsRes] = await Promise.all([
        fetch(`${API_URL}/api/stages`, { headers }),
        fetch(`${API_URL}/api/subcategories`, { headers }),
        fetch(`${API_URL}/api/prj`, { headers })
      ]);

      if (!stagesRes.ok || !subcategoriesRes.ok || !projectsRes.ok) {
        throw new Error('Помилка завантаження даних');
      }

      const stagesData = await stagesRes.json();
      const subcategoriesData = await subcategoriesRes.json();
      const projectsData = await projectsRes.json();

      setStages(stagesData);
      setSubcategories(subcategoriesData);
      setProjects(projectsData);

      // Если есть стадии, выбираем первую по умолчанию
      if (stagesData.length > 0) {
        setSelectedStageId(stagesData[0].id);
      }
    } catch (error) {
      setError('Помилка завантаження даних: ' + error.message);
    }
  };

  const fetchCategories = async (stageId) => {
    try {
      const currentUser = JSON.parse(localStorage.getItem('user'));
      if (!currentUser) throw new Error('Користувач не знайдений');
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/categories?stage_id=${stageId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response.ok) {
        throw new Error('Помилка завантаження категорій');
      }
      const data = await response.json();
      setCategories(data);
    } catch (error) {
      setError('Помилка завантаження категорій: ' + error.message);
    }
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handleOpenDialog = (type) => {
    setDialogType(type);
    setFormData({ name: '', stage_id: '' });
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setError(null);
  };

  const handleFormChange = (event) => {
    const { name, value } = event.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async () => {
    try {
      const currentUser = JSON.parse(localStorage.getItem('user'));
      if (!currentUser) throw new Error('Користувач не знайдений');
      let response;
      const token = localStorage.getItem('token');
      const headers = {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      };
      switch (dialogType) {
        case 'stage':
          response = await fetch(`${API_URL}/api/stages?user_id=${currentUser.id}`, {
            method: 'POST',
            headers,
            body: JSON.stringify({ name: formData.name })
          });
          break;
        case 'category':
          if (!formData.stage_id) {
            setError('Оберіть стадію для категорії');
            return;
          }
          response = await fetch(`${API_URL}/api/categories?user_id=${currentUser.id}`, {
            method: 'POST',
            headers,
            body: JSON.stringify({
              name: formData.name,
              stage_id: parseInt(formData.stage_id)
            })
          });
          break;
        case 'subcategory':
          response = await fetch(`${API_URL}/api/subcategories?user_id=${currentUser.id}`, {
            method: 'POST',
            headers,
            body: JSON.stringify({ name: formData.name })
          });
          break;
        case 'project':
          response = await fetch(`${API_URL}/api/prj?user_id=${currentUser.id}`, {
            method: 'POST',
            headers,
            body: JSON.stringify({ name: formData.name })
          });
          break;
        default:
          throw new Error('Невідомий тип');
      }

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Помилка при створенні');
      }

      setSuccess(`${dialogType === 'stage' ? 'Стадію' :
        dialogType === 'category' ? 'Категорію' :
          dialogType === 'subcategory' ? 'Підкатегорію' :
            'Проект'} успішно створено`);
      handleCloseDialog();
      fetchData();
      // Сброс stage_id после добавления категории
      if (dialogType === 'category') {
        setFormData(prev => ({ ...prev, stage_id: '' }));
      }
    } catch (error) {
      setError(error.message);
    }
  };

  const handleDelete = async (type, id) => {
    if (!window.confirm('Ви впевнені, що хочете видалити цей елемент?')) {
      return;
    }

    try {
      const currentUser = JSON.parse(localStorage.getItem('user'));
      if (!currentUser) throw new Error('Користувач не знайдений');
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/${type}/${id}?user_id=${currentUser.id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Помилка при видаленні');
      }

      setSuccess(`${type === 'stages' ? 'Стадію' :
        type === 'categories' ? 'Категорію' :
          type === 'subcategories' ? 'Підкатегорію' :
            'Проект'} успішно видалено`);
      fetchData();
    } catch (error) {
      setError(error.message);
    }
  };

  const handleEdit = (type, item) => {
    setEditType(type);
    setEditItem(item);
    setOpenEditDialog(true);
  };

  const handleEditSubmit = async () => {
    try {
      const currentUser = JSON.parse(localStorage.getItem('user'));
      if (!currentUser) throw new Error('Користувач не знайдений');
      const formData = new FormData();
      formData.append('name', editItem.name);
      formData.append('user_id', currentUser.id);
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/${editType}/${editItem.id}`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Помилка при редагуванні');
      }
      setSuccess(`${editType === 'stages' ? 'Стадію' :
        editType === 'categories' ? 'Категорію' :
          editType === 'subcategories' ? 'Підкатегорію' :
            'Проект'} успішно оновлено`);
      handleCloseEditDialog();
      fetchData();
    } catch (error) {
      setError(error.message);
    }
  };

  const handleCloseEditDialog = () => {
    setOpenEditDialog(false);
    setError(null);
    setEditItem(null);
  };

  if (String(userRole) !== "1") {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h6" color="error">
          У вас немає прав для доступу до цієї сторінки
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
        <Tabs 
          value={activeTab} 
          onChange={handleTabChange}
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
        >
          <Tab label="Стадії" />
          <Tab label="Категорії" />
          <Tab label="Підкатегорії" />
          <Tab label="Проекти" />
        </Tabs>
      </Box>

      <Box sx={{ mb: 2 }}>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog(
            activeTab === 0 ? 'stage' :
              activeTab === 1 ? 'category' :
                activeTab === 2 ? 'subcategory' :
                  'project')}
        >
          Додати {activeTab === 0 ? 'стадію' :
            activeTab === 1 ? 'категорію' :
              activeTab === 2 ? 'підкатегорію' :
                'проект'}
        </Button>
      </Box>

      <TableContainer component={Paper} sx={{ overflowX: 'auto' }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>ID</TableCell>
              <TableCell>Назва</TableCell>
              {activeTab === 1 && <TableCell>Стадія</TableCell>}
              <TableCell align="right">Дії</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {activeTab === 0 && stages.map((stage) => (
              <TableRow key={stage.id}>
                <TableCell>{stage.id}</TableCell>
                <TableCell>{stage.name}</TableCell>
                <TableCell align="right">
                  <IconButton
                    onClick={() => handleEdit('stages', stage)}
                  >
                    <EditIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
            {activeTab === 1 && (
              <>
                <FormControl fullWidth sx={{ mb: 2 }}>
                  <InputLabel>Стадія</InputLabel>
                  <Select
                    value={selectedStageId}
                    label="Стадія"
                    onChange={e => setSelectedStageId(e.target.value)}
                    sx={{
                      '& .MuiSelect-select': {
                        display: 'flex',
                        alignItems: 'center'
                      }
                    }}
                    startAdornment={
                      <Box sx={{ mr: 1, display: 'flex', alignItems: 'center' }}>
                        <AddIcon fontSize="small" />
                      </Box>
                    }
                  >
                    {stages.map(stage => (
                      <MenuItem key={stage.id} value={stage.id}>{stage.name}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                {categories.map((category) => (
                  <TableRow key={category.id}>
                    <TableCell>{category.id}</TableCell>
                    <TableCell>{category.name}</TableCell>
                    <TableCell>
                      {stages.find(s => s.id === selectedStageId)?.name}
                    </TableCell>
                    <TableCell align="right">
                      <IconButton
                        onClick={() => handleEdit('categories', category)}
                      >
                        <EditIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </>
            )}
            {activeTab === 2 && subcategories.map((subcategory) => (
              <TableRow key={subcategory.id}>
                <TableCell>{subcategory.id}</TableCell>
                <TableCell>{subcategory.name}</TableCell>
                <TableCell align="right">
                  <IconButton
                    onClick={() => handleEdit('subcategories', subcategory)}
                  >
                    <EditIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
            {activeTab === 3 && projects.map((project) => (
              <TableRow key={project.id}>
                <TableCell>{project.id}</TableCell>
                <TableCell>{project.name}</TableCell>
                <TableCell align="right">
                  <IconButton
                    onClick={() => handleEdit('prj', project)}
                  >
                    <EditIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={openDialog} onClose={handleCloseDialog}>
        <DialogTitle>
          Додати {dialogType === 'stage' ? 'стадію' :
            dialogType === 'category' ? 'категорію' :
              dialogType === 'subcategory' ? 'підкатегорію' :
                'проект'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="Назва"
              name="name"
              value={formData.name}
              onChange={handleFormChange}
              fullWidth
              required
            />
            {dialogType === 'category' && (
              <FormControl fullWidth required>
                <InputLabel>Стадія</InputLabel>
                <Select
                  name="stage_id"
                  value={formData.stage_id}
                  onChange={handleFormChange}
                  required
                >
                  <MenuItem value="">Оберіть стадію</MenuItem>
                  {stages.map((stage) => (
                    <MenuItem key={stage.id} value={stage.id}>
                      {stage.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Скасувати</Button>
          <Button onClick={handleSubmit} variant="contained" disabled={dialogType === 'category' && !formData.stage_id}>
            Додати
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={openEditDialog} onClose={handleCloseEditDialog}>
        <DialogTitle>Редагувати {editType === 'stages' ? 'стадію' :
          editType === 'categories' ? 'категорію' :
            editType === 'subcategories' ? 'підкатегорію' :
              'проект'}</DialogTitle>
        <DialogContent>
          {editItem && (
            <TextField
              label="Назва"
              name="name"
              value={editItem.name}
              onChange={(e) => setEditItem(prev => ({ ...prev, name: e.target.value }))}
              fullWidth
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseEditDialog}>Скасувати</Button>
          <Button onClick={handleEditSubmit} variant="contained" disabled={!editItem || !editItem.name}>
            Оновити
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default StructureManagement; 